import { User } from '@kalos/kalos-rpc';
import { Button } from '@kalos/ui';
import { createFileRoute, useNavigate, useRouter } from '@tanstack/react-router';
import { ChevronLeft } from 'lucide-react';
import { z } from 'zod';

import { searchReturnLabel, searchReturnTo } from '../../components/searchReturnTo';
import { useMobileDevice } from '../../hooks/useMedia';
import { CustomerEditDialog } from '../../modules/CustomerDetailsNew/components/CustomerEditDialog';
import { CustomerEditForm } from '../../modules/CustomerDetailsNew/components/CustomerEditForm';

export const Route = createFileRoute('/customers/add')({
  component: RouteComponent,
  // Where Back and Cancel go: the customer search this was opened from, filters and all.
  validateSearch: z.object({ returnTo: searchReturnTo }),
  head() {
    return {
      meta: [{ title: 'Add Customer' }],
    };
  },
});

function RouteComponent() {
  const navigate = useNavigate();
  const router = useRouter();
  const { returnTo } = Route.useSearch();
  const isMobileDevice = useMobileDevice();

  const close = () => (returnTo ? router.history.push(returnTo) : navigate({ to: '/customers' }));
  const openCustomer = (saved: User) =>
    navigate({
      to: '/customer-details/$customerId',
      params: { customerId: saved.id },
      search: { returnTo },
    });

  // Phones: the same form, rules and save, as a guided page in three steps.
  if (isMobileDevice) {
    return (
      <div className="bg-background min-h-full">
        <main className="mx-auto flex w-full max-w-2xl flex-col gap-4 px-4 py-4">
          <Button variant="ghost" className="-ml-3 w-fit gap-1" onClick={close}>
            <ChevronLeft className="h-4 w-4" aria-hidden />
            {returnTo ? searchReturnLabel(returnTo) : 'Back to Customer Search'}
          </Button>
          <h1 className="text-xl font-bold">Add Customer</h1>
          <div className="bg-card border-border rounded-lg border p-4">
            <CustomerEditForm
              guided
              mode="create"
              customer={User.create({ billingTerms: 'COD' })}
              groupLinks={[]}
              onSave={openCustomer}
              onCancel={close}
            />
          </div>
        </main>
      </div>
    );
  }

  return (
    <CustomerEditDialog
      open
      mode="create"
      customer={User.create({ billingTerms: 'COD' })}
      groupLinks={[]}
      onClose={close}
      onSave={openCustomer}
    />
  );
}
